import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe, Clock, Package, CheckCircle2 } from "lucide-react";
import { SEO, buildProductJsonLd, buildBreadcrumbJsonLd } from "@/components/SEO";
import { goToEnquiryAs } from "@/components/EnquiryForm";
import { VideoLightbox } from "@/components/VideoLightbox";
import { LoadRatingBadge } from "@/components/LoadRatingBadge";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { SPECS } from "@/config/hardLidSpecs";

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = "/products/hard-lids";
const fmt = (n) => n?.toLocaleString("en-NZ");

// Vehicle fitments and accessories are admin-managed (category "hardlid" in
// the generic Product collection — /admin/hard-lids) rather than hardcoded
// here, so pricing/availability/badges can be updated without a code change.

const VehicleCard = ({ v }) => {
  const urgent = v.badge === "Launch Fitment";
  return (
    <Link
      to={`/hard-lids/${v.slug}`}
      className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
      data-testid={`hard-lid-vehicle-${v.slug}`}
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={v.thumb}
          alt={`Tour Top Cover hard lid fitted to a ${v.name}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
        <span
          className={`absolute top-3 right-3 font-mono text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 flex items-center gap-1.5 ${
            urgent ? "bg-[var(--brand-primary)] text-black" : "bg-black/70 text-sky-300 border border-sky-300/30"
          }`}
        >
          <Clock size={10} /> {v.badge}
        </span>
        <div className="absolute top-3 left-3">
          <LoadRatingBadge size="sm" testId={`hard-lid-vehicle-load-badge-${v.slug}`} />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
          <h3 className="font-display text-lg text-white leading-tight">{v.name.toUpperCase()}</h3>
          <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-1">{v.variants}</p>
          <div className="flex items-end justify-between gap-3 mt-3">
            <div>
              {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && !v.priceOnRequest && v.price ? (
                <>
                  <p className="font-display text-xl text-[var(--brand-primary)] leading-none">${fmt(v.price)}</p>
                  <p className="text-[9px] font-mono uppercase tracking-widest text-white/50 mt-1">Incl GST</p>
                </>
              ) : (
                <p className="text-sm font-mono uppercase tracking-widest text-[var(--brand-primary)]">Coming Soon</p>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToEnquiryAs(`Hard Lid (Tour Top Cover) — ${v.name}${v.variants ? ` (${v.variants})` : ""}`); }}
              data-testid={`hard-lid-vehicle-cta-${v.slug}`}
              className="font-mono text-[10px] tracking-[0.15em] uppercase text-white hover:text-[var(--brand-primary)] transition-colors flex items-center gap-1 shrink-0 relative z-10"
            >
              {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && !v.priceOnRequest && v.price ? "Pre-Order Now" : "Enquire"} <ArrowUpRight size={12} />
            </button>
          </div>
          {v.tagline && <p className="text-[11px] text-white/50 mt-2 leading-snug">{v.tagline}</p>}
        </div>
      </div>
    </Link>
  );
};

// Larger, richer card for an accessory that's more fundamental than the
// typical small T-slot mount (e.g. the Tour Slider) — a photo collage
// (reusing the hero section's 3-tile collage pattern) plus full copy and
// feature bullets, spanning the full grid width ahead of the regular
// small accessory tiles.
const FeaturedAccessoryCard = ({ a }) => {
  const photos = (a.gallery || []).slice(0, 3);
  return (
    <div
      className="col-span-2 md:col-span-4 border border-[var(--border-default)] bg-[var(--bg-secondary)] grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 p-5 md:p-8"
      data-testid={`hard-lid-accessory-featured-${a.slug}`}
    >
      <div className="lg:col-span-6 grid grid-cols-3 grid-rows-2 gap-3 aspect-[4/3]">
        <div className="col-span-2 row-span-2 relative overflow-hidden border border-[var(--border-default)]">
          <img src={photos[0]?.src || a.thumb} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
        {photos.slice(1, 3).map((p, i) => (
          <div key={p.src || i} className="relative overflow-hidden border border-[var(--border-default)]">
            <img src={p.src} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
      <div className="lg:col-span-6 flex flex-col justify-center">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--brand-primary)] mb-3 w-fit">
          — Tour Top Partner
        </span>
        <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">{a.name}</h3>
        {a.tagline && <p className="text-[var(--text-secondary)] mt-3 leading-relaxed">{a.tagline}</p>}
        {a.features?.length > 0 && (
          <ul className="mt-4 space-y-2">
            {a.features.slice(0, 4).map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-[var(--text-secondary)]">
                <span className="w-1 h-1 bg-[var(--brand-primary)] mt-2 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => goToEnquiryAs(`Hard Lid Accessory — ${a.name}`)}
          data-testid={`hard-lid-accessory-featured-cta-${a.slug}`}
          className="btn-secondary w-fit mt-6"
        >
          Enquire <ArrowUpRight size={14} />
        </button>
      </div>
    </div>
  );
};

const AccessoryCard = ({ a }) => (
  <div className="group border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]" data-testid={`hard-lid-accessory-${a.slug}`}>
    <div className="aspect-square overflow-hidden">
      <img src={a.thumb} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
    </div>
    <div className="p-4">
      <h4 className="font-display text-lg text-white">{a.name}</h4>
      <p className="text-sm text-[var(--text-secondary)] mt-1.5 leading-relaxed">{a.tagline}</p>
      <button
        type="button"
        onClick={() => goToEnquiryAs(`Hard Lid Accessory — ${a.name}`)}
        data-testid={`hard-lid-accessory-cta-${a.slug}`}
        className="font-mono text-xs tracking-[0.15em] uppercase text-[var(--brand-primary)] mt-3 inline-flex items-center gap-1"
      >
        Enquire <ArrowUpRight size={10} />
      </button>
    </div>
  </div>
);

// Click-to-enlarge video component now lives in @/components/VideoLightbox
// since it's shared with HardLidDetailPage.jsx.

const GALLERY = [
  { src: `${IMG}/pod-ammo-can.webp`, alt: "An ammo can secured to the T-slot track with a ratchet strap" },
  { src: `${IMG}/open-bed-2.webp`, alt: "Jerry cans, an ammo box and a bike all mounted on the tilted-open lid at once" },
  { src: `${IMG}/pod-lid-lift-bike.webp`, alt: "Lifting the lid open to load a mountain bike onto the mounting track" },
  { src: `${IMG}/pod-wheel-strap.webp`, alt: "A bike wheel secured directly to the track with a ratchet strap" },
  { src: `${IMG}/open-bed-1.webp`, alt: "Open tub with ammo box and bike secured to the track system" },
  { src: `${IMG}/lifestyle-2.webp`, alt: "Both gas struts open, full view into the empty tub" },
  { src: `${IMG}/side-profile.webp`, alt: "Close-up of a fork-mount bike clamp attached to the T-slot track" },
  { src: `${IMG}/tracks-closeup-2.webp`, alt: "Road bike wheel strapped directly into the T-slot track" },
  { src: `${IMG}/tour-slider-jeep-gladiator.webp`, alt: "Tour Slider extended out the back of a Jeep Gladiator" },
  { src: `${IMG}/tour-slider-canopy-fit.webp`, alt: "Tour Slider pulled out underneath a fitted hard lid, loaded and ready" },
  { src: `${IMG}/tour-slider-ford-detail.webp`, alt: "Close-up of the Tour Slider fitted inside a Ford Ranger tub" },
];

export default function HardLidPage() {
  const [products, setProducts] = useState([]);
  const [showStoreCTA, setShowStoreCTA] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/products/hardlid`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
    // Reveals the "Jump to store" sticky CTA once the visitor has scrolled
    // roughly past the hero — same threshold/pattern as the Wellside
    // Canopies page's sticky "Ready to find your canopy?" CTA.
    const onScroll = () => setShowStoreCTA(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const vehicles = products.filter((p) => p.type === "vehicle");
  const accessories = products.filter((p) => p.type === "accessory" && !p.featured);
  const featuredAccessories = products.filter((p) => p.type === "accessory" && p.featured);
  const launchVehicles = vehicles.filter((v) => v.badge === "Launch Fitment");
  const specialOrderVehicles = vehicles.filter((v) => v.badge !== "Launch Fitment");

  return (
    <div data-testid="hard-lid-page">
      <SEO
        title="Tour Top Cover — Hard Lid & Roof Rack In One | Ranger, Hilux & More"
        description="Tour Top Cover is coming to New Zealand: a lockable hard lid for your ute tub that doubles as a full-length roof rack. Ford Ranger, Toyota Hilux, Navara, Gladiator and more — register your interest now."
        path="/hard-lids"
        image={`${IMG}/lock-detail.webp`}
        jsonLd={[
          buildProductJsonLd({
            name: "Tour Top Cover Hard Lid",
            description: "A lockable hard lid for ute tubs that doubles as a full-length roof rack via a continuous T-slot track system. Coming soon to New Zealand for Ford Ranger, Toyota Hilux, Nissan Navara, Jeep Gladiator and more.",
            image: `${IMG}/lock-detail.webp`,
            priceOnRequest: true,
            url: "/hard-lids",
          }),
          buildBreadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Hard Lids" }]),
        ]}
      />

      {/* STICKY "JUMP TO STORE" — appears once scrolled past the hero, same
          pattern as the Wellside Canopies page's sticky models CTA. Desktop:
          bottom-left floating card. Mobile: full-width bottom bar. */}
      <div
        data-testid="hard-lid-sticky-store-cta"
        className={`hidden md:flex fixed bottom-6 left-6 z-40 items-center gap-4 bg-[var(--bg-main)] border border-[var(--border-default)] border-l-4 border-l-[var(--brand-primary)] shadow-2xl px-6 py-4 transition-all duration-300 ${showStoreCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        <span className="font-display text-lg text-white">Ready to find your fitment?</span>
        <a href="#vehicles" data-testid="hard-lid-sticky-store-cta-link" className="btn-primary whitespace-nowrap">
          View Store <ArrowUpRight size={14} />
        </a>
      </div>
      <div
        data-testid="hard-lid-sticky-store-cta-mobile"
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--bg-main)]/95 backdrop-blur-md border-t border-[var(--border-default)] px-4 flex transition-all duration-300 ${showStoreCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        style={{ paddingTop: "0.75rem", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <a
          href="#vehicles"
          data-testid="hard-lid-sticky-store-cta-link-mobile"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[var(--brand-primary)] text-black text-xs uppercase tracking-wider py-3 font-semibold"
        >
          <Package size={14} /> View Store
        </a>
      </div>

      {/* HERO — closed-lid shot (bikes + jerry can clamped onto the locked
          lid) shows both the "locked" and "roof rack" functions at once.
          Mobile: image and text are two separate stacked blocks (text was
          too crowded overlaid on the image) - text sits on a solid dark
          panel below a shorter image crop. Desktop (md:+): text overlays
          the full-height image as before, bottom-aligned with a gradient. */}
      <section className="relative overflow-hidden md:min-h-[90svh] md:flex md:items-end">
        <div className="relative h-[40svh] md:absolute md:inset-0 md:h-full">
          <img
            src={`${IMG}/lock-detail.webp`}
            alt="Tour Top Cover hard lid closed and locked on a Ford Ranger, with bikes and a jerry can mounted on top via the T-slot track system"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "65% 30%" }}
            data-testid="hard-lid-hero-image"
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0%,transparent_72%,black_100%)] md:bg-gradient-to-t md:from-black/60 md:via-black/15 md:to-transparent" />
        </div>
        <div className="relative bg-black md:bg-transparent w-full">
          <div className="relative container-x py-5 md:pb-16 md:pt-32 w-full">
            <div className="hidden md:block absolute bottom-12 md:bottom-16 right-6 md:right-12 z-10">
              <LoadRatingBadge caption="Across The Range" testId="hard-lid-hero-load-badge" />
            </div>
            <span
              data-testid="hard-lid-coming-soon-badge"
              className="block w-fit font-mono text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 border bg-black/60 backdrop-blur-sm text-sky-300 border-sky-300/40 mb-2 md:mb-5"
            >
              Coming Soon To New Zealand
            </span>
            <p className="overline mb-1.5 md:mb-4">Hard Lids · Tour Top Cover</p>
            <h1 className="font-display text-3xl md:text-6xl leading-[0.95] text-white hero-text-shadow max-w-4xl">
              LOCK IT. LOAD IT. <span className="text-[var(--brand-primary)]">MOUNT IT ANYWHERE.</span>
            </h1>
            <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-2xl mt-2.5 md:mt-6">
              The security of a locking hard lid and the load-carrying versatility of a high-end roof rack — built as one integrated product, with mounting tracks across the top, around the perimeter and on the inside of the lid.
            </p>
            <div className="flex flex-wrap gap-3 mt-3.5 md:mt-8">
              <a href="#vehicles" data-testid="hard-lid-cta-hero" className="btn-primary">
                Shop By Vehicle <ArrowUpRight size={14} />
              </a>
              <button type="button" onClick={() => goToEnquiryAs("Hard Lid (Tour Top Cover)")} data-testid="hard-lid-cta-hero-enquire" className="btn-secondary">
                Enquire
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* MUCH MORE THAN A HARD LID — placed right after the hero, before the
          store. Media is a hero-tile + 2 stacked tiles grid (big top-track
          photo, walkaround video, underside-tracks photo) rather than a
          plain vertical stack of same-size tiles. */}
      <section className="container-x pt-12 md:pt-16 pb-4 md:pb-6" data-testid="hard-lid-differentiator">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-6 order-1">
            <p className="overline mb-3">— The Point Of Difference</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              MUCH MORE <span className="text-[var(--brand-primary)]">THAN A HARD LID.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-5 leading-relaxed text-lg">
              Most hard lids secure your tub and offer a handful of mounting points. Tour Top takes a completely different approach.
            </p>
            <p className="text-[var(--text-secondary)] mt-4 leading-relaxed text-lg">
              Integrated T-slot tracks run across the top, around the perimeter and on the inside of the lid — giving you the mounting flexibility of a high-end roof rack, backed by an impressive <span className="text-white font-semibold">250kg dynamic load capacity</span>.
            </p>
            <p className="text-white font-semibold mt-5 leading-relaxed text-lg">
              Mount gear on top. Secure more underneath. All from one lid.
            </p>
            <p className="text-[var(--text-secondary)] mt-4 leading-relaxed text-lg">
              Built tough in South Africa, with premium construction and exceptional value for money.
            </p>
          </div>
          <div className="lg:col-span-6 order-2">
            <div className="grid grid-cols-3 grid-rows-2 gap-3 md:gap-4 aspect-[4/3]" data-testid="hard-lid-pod-media">
              <div className="col-span-2 row-span-2 relative overflow-hidden border border-[var(--border-default)]">
                <img
                  src={`${IMG}/tracks-closeup-1.webp`}
                  alt="Close-up of the T-slot track system with a bike fork mount clamped directly to the lid"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.15em] uppercase text-white bg-black/60 px-2 py-1 backdrop-blur-sm">Top Track System</span>
              </div>
              <div className="relative overflow-hidden border border-[var(--border-default)]" data-testid="hard-lid-strut-detail">
                <img
                  src={`${IMG}/pod-gas-strut.webp`}
                  alt="Gas struts holding the lid open for full access to the tub"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.15em] uppercase text-white bg-black/60 px-2 py-1 backdrop-blur-sm">Easy Lift Access</span>
              </div>
              <div className="relative overflow-hidden border border-[var(--border-default)]" data-testid="hard-lid-underside-tracks">
                <img
                  src={`${IMG}/rear-view.webp`}
                  alt="The underside of the Tour Top lid, showing the four integrated mounting tracks most hard lids don't have"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.15em] uppercase text-white bg-black/60 px-2 py-1 backdrop-blur-sm">Underside Tracks</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT IN SOUTH AFRICA — desktop/tablet only (combined with the
          walkaround video, side by side). On mobile this same content is
          split into two separate pieces placed elsewhere on the page (see
          "hard-lid-origin-video-mobile" and "hard-lid-origin-text-mobile"
          below) to reduce scroll-to-store: the video moves above the store,
          the text moves after it. Credibility/quality is still established
          before anyone sees a price either way. */}
      <section className="hidden lg:block container-x pt-4 md:pt-6 pb-4 md:pb-6" data-testid="hard-lid-origin">
        <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 lg:items-stretch">
          <div className="lg:col-span-6 flex flex-col sm:flex-row gap-6 md:gap-8">
            <Globe size={36} className="text-[var(--brand-primary)] shrink-0" strokeWidth={1.5} />
            <div>
              <p className="overline mb-3">— Where It's Built</p>
              <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-3">
                BUILT IN SOUTH AFRICA. <span className="text-[var(--brand-primary)]">BUILT TOUGH.</span>
              </h3>
              <p className="text-[var(--text-secondary)] leading-relaxed text-lg">
                TourTop is engineered and manufactured in South Africa, where long-distance 4WD touring demands equipment that stands up to dust, corrugated roads, heavy loads and harsh weather.
              </p>
              <p className="text-[var(--text-secondary)] leading-relaxed text-lg mt-4">
                It's the same emphasis on strength, practical engineering and durability that defines the premium South African roof tents and Bushtech aluminium canopies we bring to New Zealand.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6" data-testid="hard-lid-origin-video">
            <div className="relative w-full h-full aspect-video lg:aspect-auto min-h-[260px] md:min-h-[360px] lg:min-h-0 overflow-hidden border border-[var(--border-default)] bg-black">
              <VideoLightbox
                videoId="9K6vrdAClI8"
                title="Tour Top Cover — The point of difference, demonstrated"
                testId="hard-lid-video"
                thumbnail={`${IMG}/presenter-opening-lid.webp`}
                startSeconds={6}
                caption="A Quick Walkaround"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MOBILE-ONLY: walkaround video, moved above the store so it sells the
          point of difference fast right before the vehicle grid, instead of
          making mobile visitors scroll past the full origin story first. */}
      <section className="lg:hidden container-x pt-4 md:pt-6 pb-4 md:pb-6" data-testid="hard-lid-origin-video-mobile">
        <div className="relative w-full aspect-video overflow-hidden border border-[var(--border-default)] bg-black">
          <VideoLightbox
            videoId="9K6vrdAClI8"
            title="Tour Top Cover — The point of difference, demonstrated"
            testId="hard-lid-video-mobile"
            thumbnail={`${IMG}/presenter-opening-lid.webp`}
            startSeconds={6}
            caption="A Quick Walkaround"
          />
        </div>
      </section>


      {/* VALUE FOR MONEY — a single compact statement rather than a full bordered
          callout card, so it doesn't eat vertical space between the South
          Africa callout and the store. Repeats the 250kg load rating here
          too (not just in the specs table further down). Hidden while
          pricing isn't confirmed yet — this is a price argument and doesn't
          make sense with no figure shown anywhere on the page. */}
      {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && (
      <section className="container-x pt-0 pb-4 md:pb-6" data-testid="hard-lid-value">
        <p className="max-w-3xl text-white font-semibold leading-relaxed text-lg">
          Priced about what you'd pay for a mid-range basic hard lid, Tour Top Cover is rated to carry <span className="text-[var(--brand-primary)]">250kg dynamic</span> — across a continuous, full-length aluminium track system, not just a couple of fixed points. Same ballpark price, genuinely superior build quality and functionality.
        </p>
      </section>
      )}

      {/* VEHICLE FITMENTS */}
      <section id="vehicles" className="container-x pt-4 md:pt-6 pb-12 md:pb-16 scroll-mt-24" data-testid="hard-lid-vehicles">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline mb-4">— Shop By Vehicle</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
              FIT YOUR UTE.
            </h2>
          </div>
        </div>

        {/* CONFIRMED FOR LAUNCH — wrapped in its own highlighted box (brand-
            colour left border + icon + larger heading) so it reads as the
            priority group at a glance, not just a smaller text label above
            an otherwise identical grid. */}
        <div className="border-l-4 border-[var(--brand-primary)] bg-[var(--bg-secondary)] pl-5 pr-4 md:pl-8 md:pr-6 py-6 md:py-8 mb-14" data-testid="hard-lid-vehicles-launch-group">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 size={24} className="text-[var(--brand-primary)] shrink-0" strokeWidth={1.8} />
            <div>
              <h3 className="font-display text-2xl md:text-3xl text-white leading-tight" data-testid="hard-lid-vehicles-launch-heading">
                Confirmed For Launch
              </h3>
              <p className="text-base md:text-lg text-[var(--text-secondary)] mt-1.5">These three fitments are locked in and landing <span className="text-white font-semibold">end of January</span> — first to arrive in NZ.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {launchVehicles.map((v) => (
              <VehicleCard key={v.slug} v={v} />
            ))}
          </div>
        </div>

        {/* SPECIAL ORDER — separated by a full-width rule and deliberately
            more subdued (no border/tint, muted heading colour) so it reads
            as secondary to the launch lineup above. */}
        <div className="border-t border-[var(--border-default)] pt-10" data-testid="hard-lid-vehicles-special-order-group">
          <div className="flex items-center gap-3 mb-6">
            <Package size={22} className="text-[var(--text-tertiary)] shrink-0" strokeWidth={1.8} />
            <div>
              <h3 className="font-display text-xl md:text-2xl text-white/70 leading-tight" data-testid="hard-lid-vehicles-request-heading">
                Special Order Fitments
              </h3>
              <p className="text-base md:text-lg text-[var(--text-secondary)] mt-1.5">Need a fitment outside our standard launch stock? <span className="text-white font-semibold">We're accepting pre-orders now</span> — order in time and yours could land in the same end-of-January shipment.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {specialOrderVehicles.map((v) => (
              <VehicleCard key={v.slug} v={v} />
            ))}
          </div>
        </div>
      </section>

      {/* MOBILE-ONLY: "Built In South Africa" origin story, moved after the
          store — valuable trust content for someone still deciding, but no
          longer gating mobile visitors' first look at the vehicle grid. */}
      <section className="lg:hidden container-x pt-4 md:pt-6 pb-4 md:pb-6" data-testid="hard-lid-origin-text-mobile">
        <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6 flex flex-col sm:flex-row gap-6">
          <Globe size={36} className="text-[var(--brand-primary)] shrink-0" strokeWidth={1.5} />
          <div>
            <p className="overline mb-3">— Where It's Built</p>
            <h3 className="font-display text-2xl text-white leading-tight mb-3">
              BUILT IN SOUTH AFRICA. <span className="text-[var(--brand-primary)]">BUILT TOUGH.</span>
            </h3>
            <p className="text-[var(--text-secondary)] leading-relaxed text-lg">
              TourTop is engineered and manufactured in South Africa, where long-distance 4WD touring demands equipment that stands up to dust, corrugated roads, heavy loads and harsh weather.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed text-lg mt-4">
              It's the same emphasis on strength, practical engineering and durability that defines the premium South African roof tents and Bushtech aluminium canopies we bring to New Zealand.
            </p>
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="container-x pb-12 md:pb-16" data-testid="hard-lid-specs">
        <div className="mb-8 max-w-2xl">
          <p className="overline mb-3">— Build & Specification</p>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
            SPECS THAT <span className="text-[var(--brand-primary)]">BACK IT UP.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {SPECS.map((s) => (
            <div key={s.label} className="bg-[var(--bg-main)] p-5 md:p-7" data-testid={`hard-lid-spec-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <s.icon size={20} className="text-[var(--brand-primary)] mb-3" strokeWidth={1.8} />
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)]">{s.label}</p>
              <p className="font-display text-xl md:text-2xl text-white mt-1 leading-tight">{s.value}</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">{s.sub}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-[var(--text-tertiary)] mt-4 max-w-2xl">
          Specifications based on the manufacturer's current release.
        </p>
      </section>

      {/* GALLERY */}
      <section className="container-x pb-12 md:pb-16" data-testid="hard-lid-gallery">
        <div className="mb-8 max-w-2xl">
          <p className="overline mb-3">— A Closer Look</p>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
            BUILT TO CARRY <span className="text-[var(--brand-primary)]">WHATEVER YOU BRING.</span>
          </h2>
        </div>
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          <div className="relative aspect-square overflow-hidden border border-[var(--border-default)]" data-testid="hard-lid-gallery-video-tile">
            <VideoLightbox videoId="9K6vrdAClI8" title="Tour Top Cover — The point of difference, demonstrated" testId="hard-lid-gallery-video" />
          </div>
          {GALLERY.map((g, i) => (
            <div key={g.src} className="relative aspect-square overflow-hidden border border-[var(--border-default)]" data-testid={`hard-lid-gallery-image-${i}`}>
              <img src={g.src} alt={g.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* ACCESSORIES */}
      <section className="container-x pb-12 md:pb-16" data-testid="hard-lid-accessories">
        <div className="mb-8 max-w-2xl flex items-start gap-3">
          <Package size={28} className="text-[var(--brand-primary)] shrink-0 mt-1" strokeWidth={1.5} />
          <div>
            <p className="overline mb-3">— Build It Out</p>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
              ACCESSORIES FOR <span className="text-[var(--brand-primary)]">EVERY T-SLOT.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-4 leading-relaxed text-lg">
              The same track system that makes the lid a roof rack also takes a full range of mounts — clamp gear down anywhere, no drilling. NZ pricing confirmed closer to launch.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {featuredAccessories.map((a) => (
            <FeaturedAccessoryCard key={a.slug} a={a} />
          ))}
          {accessories.map((a) => (
            <AccessoryCard key={a.slug} a={a} />
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-y border-[var(--border-default)] bg-[var(--bg-secondary)]" data-testid="hard-lid-final-cta">
        <div className="container-x py-12 md:py-16 text-center">
          <p className="overline mb-4">— Be First To Know</p>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95] max-w-2xl mx-auto">
            WANT ONE WHEN IT <span className="text-[var(--brand-primary)]">LANDS?</span>
          </h2>
          <p className="text-[var(--text-secondary)] mt-5 max-w-xl mx-auto leading-relaxed text-lg">
            Leave your details and we'll contact you directly as soon as Tour Top Cover is available to order in New Zealand.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            <button type="button" onClick={() => goToEnquiryAs("Hard Lid (Tour Top Cover)")} data-testid="hard-lid-cta-final" className="btn-primary">
              Register Your Interest <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
